import apiClient from "src/utils/apiClient";
import {
  popVideoUploadQueue,
  pushVideoUploadQueue,
} from "src/store/createAssessmentSlice";
import store from "src/store/store";
import { createToast } from "src/store/toastsSlice";
import { TOAST_TIMEOUT, TRA_ERRORS_MAPPINGS } from "src/constants/constants";
import { BioMechanicsCsvJson } from "./types/biomechanicsCsvJson";
import { DataDeepDive } from "src/store/assessmentDetailSlice";
import {
  LMEMetadataDetail,
  SaveLMEMetadataRequest,
} from "./types/lmeMetadataDetail";

export interface GetMetadataOptionsRequest {
  metadataType: string;
}

export interface MetadataOption {
  id: string;
  description: string;
}

export interface ActivityScoreUnit extends MetadataOption {
  isSelected: boolean;
}

export interface RebaMetadataOptions {
  couplingOptions: MetadataOption[];
  activityScoreOptions: MetadataOption[];
  forceLoadOptions: MetadataOption[];
}
export interface RulaMetadataOptions {
  forceLoadOptions: MetadataOption[];
  muscleUseOptions: MetadataOption[];
}

export interface CreateAssessmentRequest {
  owner: string;
  clientId: string;
  site: string;
  processPath: string;
  activity: string;
  type: string;
  siteType: string;
  organization: string;
  subOrganization: string;
  region: string;
  country: string;
}

export interface ReplaceEvaluationRequest {
  owner: string;
  clientId: string;
  evaluationIdToReplace: string;
  assessmentId: string;
}

export interface CreateAssessmentResponse {
  initialEvaluationId: string;
  followupEvaluationId: string;
  assessmentId: string;
}

enum OptionSelectOne {
  NOT_PICK = -1,
  OPTION_1 = 0,
  OPTION_2 = 1,
}

enum OptionYesAndNo {
  NO = 0,
  YES = 1,
}

export interface PrepareForVideoUploadRequest {
  owner: string;
  clientId: string; //only mandatory for clientId: HFE
  evaluationId: string;
  forceLoadSelection: string;
  isShockOrRapidBuildUpForce: OptionYesAndNo;
  couplingSelection: string;
  activitySelections: string[];
  rightWristAwkwardPosition: OptionSelectOne;
  leftWristAwkwardPosition: OptionSelectOne;
  rightWristBentTwisted: OptionYesAndNo;
  leftWristBentTwisted: OptionYesAndNo;
  comment: string;
}

export interface ErgonetPrepareForVideoUploadRequest {
  owner: string;
  clientId: string;
  evaluationId: string;
  forceLoadSelection: string;
  isShockOrRapidBuildUpForce: number;
  couplingSelection: string;
  activitySelections: string[];
  isRightArmSupportedOrPersonLeaning: number;
  isLeftArmSupportedOrPersonLeaning: number;
  comment: string;
}

export interface PrepareForRulaVideoUploadRequest {
  owner: string;
  clientId: string; //only mandatory for clientId: HFE
  evaluationId: string;
  isLeftArmSupportedOrPersonLeaning: number;
  isLeftLowerArmAcrossMidlineOrOutToSide: number;
  isRightArmSupportedOrPersonLeaning: number;
  isRightLowerArmAcrossMidlineOrOutToSide: number;
  rightWristAwkwardPosition: number;
  leftWristAwkwardPosition: number;
  leftWristTwistPosition: number;
  rightWristTwistPosition: number;
  leftWristBentTwisted: number;
  rightWristBentTwisted: number;
  armMuscleUseSelection: string;
  armForceLoadSelection: string;
  isNeckTwisted: number;
  isTrunkTwisted: number;
  isLegFeetNotSupported: number;
  lowerBodyMuscleUseSelection: string;
  lowerBodyForceLoadSelection: string;
  comment: string;
}

export interface ErgonetPrepareForRulaVideoUploadRequest {
  owner: string;
  clientId: string;
  evaluationId: string;
  isLeftArmSupportedOrPersonLeaning: number;
  isRightArmSupportedOrPersonLeaning: number;
  armMuscleUseSelection: string;
  armForceLoadSelection: string;
  lowerBodyMuscleUseSelection: string;
  lowerBodyForceLoadSelection: string;
  comment: string;
}

export interface PrepareForVideoUploadResponse {
  evaluationId: string;
  preSignedUrlForUpload: string;
  presignedUrlForErgoNetUpload: string;
}

export interface GetAssessmentDetailByIdRequest {
  owner: string;
  assessmentId: string;
}

export interface GetEvaluationDetailByIdRequest {
  owner: string;
  evaluationId: string;
}

export interface EvaluationUrl {
  preSignedUrlForBiomechanics: string;
  preSignedUrlForDemoVideo: string;
  preSignedUrlForRebaScores: string;
  preSignedUrlForAssessmentScores: string;
}

export interface Evaluation {
  activity: number;
  assessmentId: string;
  clientId: string;
  coupling: number;
  evaluationId: string;
  forceLoad: number;
  highestRebaScore: number;
  isShockOrRapidBuildUpForce: number;
  leftWristAwkwardPosition: number;
  leftWristBentTwisted: number;
  owner: string;
  rightWristAwkwardPosition: number;
  rightWristBentTwisted: number;
  status: string;
  statusUpdateDate: number;
  errorMessage: keyof typeof TRA_ERRORS_MAPPINGS;
}

export interface EvaluationDetail {
  evaluationId: string;
  owner: string;
  status: string;
  errorMessage: string;
  cliendId: string;
  assessmentId: string;
  forceLoadSelection: string;
  couplingSelection: string;
  activitySelections: string[];
  rightWristAwkwardPosition: number;
  leftWristAwkwardPosition: number;
  rightWristBentTwisted: number;
  leftWristBentTwisted: number;
  isShockOrRapidBuildUpForce: number;
  isNeckTwisted: number;
  isTrunkTwisted: number;
  isLeftArmSupportedOrPersonLeaning: number;
  isRightArmSupportedOrPersonLeaning: number;
  comment: string;
  highestRebaScore: number;
  statusUpdateDate: number;
  armForceLoadSelection: string; //
  armMuscleUseSelection: string; //
  highestScore: number; //
  isLeftLowerArmAcrossMidlineOrOutToSide: number; //
  isLegFeetNotSupported: number; //
  isRightLowerArmAcrossMidlineOrOutToSide: number; //
  leftWristTwistPosition: number; //
  lowerBodyForceLoadSelection: string; //
  lowerBodyMuscleUseSelection: string; //
  rightWristTwistPosition: number; //
  lmeMetadata?: LMEMetadataDetail | null;
}
export interface RebaScoreCSV {
  timeStamp: string;
  rebaScore: string;
  neckScore: string;
  trunkScore: string;
  legScore: string;
  upperArmScore: string;
  lowerArmScore: string;
  wristScore: string;
  loading: string;
  coupling: string;
}
export interface RulaScoreCSV {
  timeStamp: string;
  rulaScore: string;
  neckScore: string;
  trunkScore: string;
  legScore: string;
  upperArmScore: string;
  lowerArmScore: string;
  wristScore: string;
}
export interface EvaluationData {
  biomechanics: object;
  rebaScores: RebaScoreCSV[];
  rulaScores: RulaScoreCSV[];
  videoBlob: Blob | null;
}
export interface RulaEvaluationData {
  biomechanics: object;
  rulaScores: RebaScoreCSV[];
  videoBlob: Blob | null;
}
export interface PrintablePdfData {
  assessmentDetail: AssessmentDetail;
  initialEvaluationDetail: EvaluationDetail;
  followupEvaluationDetail: EvaluationDetail;
  initialEvaluationData: EvaluationData;
  followupEvaluationData: EvaluationData;
  initialEvaluationDeepDive: DataDeepDive;
  followupEvaluationDeepDive: DataDeepDive;
}

export const INITIAL: string = "initial";
export const FOLLOWUP = "followup";
export const REBA_CSV = "reba";
export const RULA_CSV = "rula";
export const BIOMECHANIC_CSV = "biomechanics";

export interface AssessmentDetail {
  activity: string;
  assessmentId: string;
  clientId: string;
  country: string;
  initialEvaluation: Evaluation;
  followupEvaluation: Evaluation;
  organization: string;
  owner: string;
  processPath: string;
  region: string;
  site: string;
  siteType: string;
  subOrganization: string;
  type: string;
  lmeActivityType?: string;
}

export interface GetPreSignedUrlForDownloadRequest {
  owner: string;
  evaluationId: string;
}

const VIDEO_UPLOAD_SUCESS = [
  "Video upload completed successfully.",
  " You may now close/refresh tab without causing issues with video processing.",
];

const VIDEO_UPLOAD_FAILURE = [
  "Video upload has failed.",
  " Please go to 'Assessment Library' to view error message with further instructions.",
];

class AssessmentService {
  async getMetadataOptions(payload: GetMetadataOptionsRequest) {
    return await apiClient.get(`/getMetadataOptions/HFE`, {
      params: payload,
    });
  }

  async createAssessment(payload: CreateAssessmentRequest) {
    return await apiClient.post(`/createAssessment/HFE`, payload);
  }

  async replaceEvaluationInAssessment(payload: ReplaceEvaluationRequest) {
    return await apiClient.post(`/replaceEvaluationInAssessment/HFE`, payload);
  }

  // Used for both the initial LME save and any follow-up save - caller
  // decides which evaluationId to target (initialEvaluationId vs
  // followupEvaluationId). clientId is a path param, not a body field.
  async saveLMEMetadata(payload: SaveLMEMetadataRequest) {
    const { clientId, ...body } = payload;
    return await apiClient.post(`/saveLMEMetadata/${clientId}`, body);
  }

  async prepareForVideoUpload(
    payload: PrepareForVideoUploadRequest | ErgonetPrepareForVideoUploadRequest,
  ) {
    return await apiClient.post(`/prepareForVideoUpload/HFE`, payload);
  }
  async prepareRulaForVideoUpload(
    payload:
      | PrepareForRulaVideoUploadRequest
      | ErgonetPrepareForRulaVideoUploadRequest,
  ) {
    return await apiClient.post(`/prepareForVideoUpload/HFE`, payload);
  }

  async uploadVideoToErgoNet(preSignedUrl: string, file: File) {
    store.dispatch(pushVideoUploadQueue(preSignedUrl));
    try {
      const response = await fetch(preSignedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
          "x-amz-expected-bucket-owner": "453145159581",
        },
      });
      if (!response.ok) {
        throw new Error(
          `ErgoNet video upload failed with status ${response.status}`,
        );
      }
      store.dispatch(
        createToast({
          type: "success",
          message: VIDEO_UPLOAD_SUCESS,
          timeout: TOAST_TIMEOUT,
        }),
      );
      return response;
    } catch (error) {
      store.dispatch(
        createToast({
          type: "error",
          message: VIDEO_UPLOAD_FAILURE,
          timeout: TOAST_TIMEOUT,
        }),
      );
      throw error;
    } finally {
      store.dispatch(popVideoUploadQueue(preSignedUrl));
    }
  }

  async getAssessmentDetailById(payload: GetAssessmentDetailByIdRequest) {
    return await apiClient.get(`/getAssessmentDetailById/HFE`, {
      params: payload,
    });
  }

  async getEvaluationDetailById(payload: GetEvaluationDetailByIdRequest) {
    return await apiClient.get(`/getEvaluationDetailById/HFE`, {
      params: payload,
    });
  }

  async getPreSignedUrlForDownload(payload: GetPreSignedUrlForDownloadRequest) {
    return await apiClient.get(`/getPreSignedUrlForDownload/HFE`, {
      params: payload,
    });
  }

  async getAllAssessmentsByClientId(clientId: string, owner: string) {
    // owner here doesn't matter but the API needs it for some reason
    // it can be any string.
    return await apiClient.get(
      `getAssessmentDetailsByClientId/${clientId}?owner=${owner}`,
    );
  }

  async getMyAssessments(clientId: string, owner: string) {
    return await apiClient.get(
      `getAssessmentDetailsByOwner/${clientId}?owner=${owner}`,
    );
  }

  async getSiteAssessments(clientId: string, owner: string, site: string) {
    return await apiClient.get(
      `getAssessmentDetailsByFilters/${clientId}/${site}?owner=${owner}`,
    );
  }

  getBodyPartJointAnglesFromCsvJson(biomechanicsCsv: BioMechanicsCsvJson[]) {
    return biomechanicsCsv.map((csvRowJson) => {
      return {
        lElbow: csvRowJson.L_Elbow,
        lKneee: csvRowJson.L_Knee,
        lShoulderLat: csvRowJson.L_Shoulder_lat,
        lShoulderSag: csvRowJson.L_Shoulder_sag,
        neckLat: csvRowJson.Neck_lat,
        neckSag: csvRowJson.Neck_sag,
        rElbow: csvRowJson.R_Elbow,
        rKnee: csvRowJson.R_Knee,
        rShoulderLat: csvRowJson.R_Shoulder_lat,
        rShoulderSag: csvRowJson.R_Shoulder_sag,
        trunkLat: csvRowJson.Trunk_lat,
        trunkSag: csvRowJson.Trunk_sag,
        trunkTwist: csvRowJson.Trunk_twist,
        personOrientation: csvRowJson.person_orientation,
        timeStamp: csvRowJson.time_stamp,
      };
    });
  }
}

export default new AssessmentService();
